import { memo } from "react";
import { Box, Container, Typography } from "@mui/material";
import { FullPaddingWrapper } from "../../Components/FullPaddingWrapper/FullPaddingWrapper";

const TermsPrivacy = memo(() => {
  return (
    <Container maxWidth="xl" disableGutters>
      <FullPaddingWrapper>
        {/* Hero Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #004B4C 0%, #006668 100%)",
            borderRadius: { xs: "16px", md: "24px" },
            p: { xs: 4, sm: 6, md: 8 },
            mb: { xs: 4, md: 6 },
          }}
        >
          <Typography
            variant="h2"
            sx={{
              color: "#ffffff",
              fontWeight: 700,
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
              lineHeight: 1.2,
              mb: 2,
            }}
          >
            Terms of Service & Privacy Policy
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#ffffff",
              fontSize: { xs: "1rem", md: "1.1rem" },
              opacity: 0.9,
            }}
          >
            Last updated: January 2025
          </Typography>
        </Box>

        {/* Content Section */}
        <Box
          sx={{
            px: { xs: 2, md: 6, lg: 10 },
            py: { xs: 4, md: 6 },
            maxWidth: 1000,
            mx: "auto",
          }}
        >
          {/* Terms of Service */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h4"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "2rem" },
                mb: 4,
              }}
            >
              Terms of Service
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{ color: "#333", fontWeight: 600, mb: 2 }}
              >
                1. Acceptance of Terms
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#555", lineHeight: 1.8, mb: 2 }}
              >
                By accessing or using SimplerHome's services, website, or mobile
                applications, you agree to be bound by these Terms of Service.
                If you do not agree to these terms, please do not use our
                services.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{ color: "#333", fontWeight: 600, mb: 2 }}
              >
                2. Description of Services
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#555", lineHeight: 1.8, mb: 2 }}
              >
                SimplerHome provides a home management platform that allows
                users to track maintenance, store documents, manage repairs, and
                access home-related services. We may modify, suspend, or
                discontinue any aspect of our services at any time.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{ color: "#333", fontWeight: 600, mb: 2 }}
              >
                3. User Accounts
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#555", lineHeight: 1.8, mb: 2 }}
              >
                You are responsible for maintaining the confidentiality of your
                account credentials and for all activities that occur under your
                account. You must provide accurate and complete information when
                creating an account and keep your information updated.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{ color: "#333", fontWeight: 600, mb: 2 }}
              >
                4. User Content
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#555", lineHeight: 1.8, mb: 2 }}
              >
                You retain ownership of any content you upload to SimplerHome,
                including documents, photos, and records. By uploading content,
                you grant us a license to store, process, and display that
                content as necessary to provide our services.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{ color: "#333", fontWeight: 600, mb: 2 }}
              >
                5. Prohibited Conduct
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#555", lineHeight: 1.8, mb: 2 }}
              >
                You agree not to use our services to: violate any laws; infringe
                on intellectual property rights; upload malicious code; attempt
                unauthorized access; harass or harm others; or engage in
                fraudulent activities.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{ color: "#333", fontWeight: 600, mb: 2 }}
              >
                6. Limitation of Liability
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#555", lineHeight: 1.8, mb: 2 }}
              >
                SimplerHome is provided "as is" without warranties of any kind.
                We are not liable for any indirect, incidental, or consequential
                damages arising from your use of our services. Our total
                liability shall not exceed the amount you paid us in the past
                twelve months.
              </Typography>
            </Box>
          </Box>

          {/* Privacy Policy */}
          <Box>
            <Typography
              variant="h4"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "2rem" },
                mb: 4,
                pt: 4,
                borderTop: "1px solid #eee",
              }}
            >
              Privacy Policy
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{ color: "#333", fontWeight: 600, mb: 2 }}
              >
                1. Information We Collect
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#555", lineHeight: 1.8, mb: 2 }}
              >
                We collect information you provide directly, such as your name,
                email, property address, and documents you upload. We also
                collect usage data, device information, and cookies to improve
                our services.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{ color: "#333", fontWeight: 600, mb: 2 }}
              >
                2. How We Use Your Information
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#555", lineHeight: 1.8, mb: 2 }}
              >
                We use your information to: provide and improve our services;
                send maintenance reminders and alerts; communicate with you
                about your account; personalize your experience; and ensure
                platform security.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{ color: "#333", fontWeight: 600, mb: 2 }}
              >
                3. Information Sharing
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#555", lineHeight: 1.8, mb: 2 }}
              >
                We do not sell your personal information. We may share
                information with service providers who assist in operating our
                platform, when required by law, or with your consent. Service
                providers are bound by confidentiality agreements.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{ color: "#333", fontWeight: 600, mb: 2 }}
              >
                4. Data Security
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#555", lineHeight: 1.8, mb: 2 }}
              >
                We implement industry-standard security measures to protect your
                data, including encryption, secure servers, and access controls.
                However, no method of transmission over the Internet is 100%
                secure.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{ color: "#333", fontWeight: 600, mb: 2 }}
              >
                5. Your Rights
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#555", lineHeight: 1.8, mb: 2 }}
              >
                You have the right to access, correct, or delete your personal
                information. You can export your data at any time. To exercise
                these rights, contact us through your account settings or at our
                support email.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{ color: "#333", fontWeight: 600, mb: 2 }}
              >
                6. Cookies and Tracking
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#555", lineHeight: 1.8, mb: 2 }}
              >
                We use cookies and similar technologies to remember your
                preferences, analyze site traffic, and personalize content. You
                can control cookie settings through your browser preferences.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{ color: "#333", fontWeight: 600, mb: 2 }}
              >
                7. Changes to This Policy
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#555", lineHeight: 1.8, mb: 2 }}
              >
                We may update this privacy policy from time to time. We will
                notify you of significant changes via email or through our
                platform. Your continued use after changes constitutes
                acceptance.
              </Typography>
            </Box>

            <Box
              sx={{
                mt: 6,
                p: 4,
                borderRadius: "12px",
                backgroundColor: "#f8f8f8",
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: "primary.main", fontWeight: 600, mb: 2 }}
              >
                Contact Us
              </Typography>
              <Typography variant="body1" sx={{ color: "#555", lineHeight: 1.8 }}>
                If you have questions about these terms or our privacy
                practices, please contact us at privacy@simplerhome.com or
                through our support channels.
              </Typography>
            </Box>
          </Box>
        </Box>
      </FullPaddingWrapper>
    </Container>
  );
});

export default TermsPrivacy;
